import type { SeoScoreContext } from './context';
import { SeoRuleKey, SeoRuleStatus, SeoScoreGroup } from './types';

/** Résultat brut d'une règle (le scoreur ajoute `key` et calcule `blocking`). */
export interface SeoRuleOutcome {
  status: SeoRuleStatus;
  value?: string | number;
  threshold?: string;
  locations?: string[];
  /** Force le caractère bloquant (ex. densité > 3,5 %) même si la règle n'est pas dans BLOCKING_RULES. */
  forceBlocking?: boolean;
}

export interface SeoRule {
  key: SeoRuleKey;
  group: SeoScoreGroup;
  /** `null` = non évaluable (SKIPPED). */
  run: (ctx: SeoScoreContext) => SeoRuleOutcome | null;
}

export const pass = (value?: string | number, threshold?: string, locations?: string[]): SeoRuleOutcome => ({
  status: SeoRuleStatus.PASS,
  value,
  threshold,
  locations,
});
export const warn = (value?: string | number, threshold?: string, locations?: string[]): SeoRuleOutcome => ({
  status: SeoRuleStatus.WARN,
  value,
  threshold,
  locations,
});
export const fail = (
  value?: string | number,
  threshold?: string,
  locations?: string[],
  forceBlocking?: boolean,
): SeoRuleOutcome => ({ status: SeoRuleStatus.FAIL, value, threshold, locations, forceBlocking });

/** Statut selon une plage PASS / WARN (bornes incluses). */
export function rangeStatus(
  value: number,
  passRange: readonly [number, number],
  warnRange: readonly [number, number],
  format: (v: number) => string | number = (v): number => v,
  locations?: string[],
): SeoRuleOutcome {
  const threshold = `${passRange[0]}–${passRange[1]}`;
  if (value >= passRange[0] && value <= passRange[1]) return pass(format(value), threshold, locations);
  if (value >= warnRange[0] && value <= warnRange[1]) return warn(format(value), threshold, locations);
  return fail(format(value), threshold, locations);
}

/** Statut « plus petit = mieux » : PASS ≤ a, WARN ≤ b, FAIL au-delà. */
export function maxStatus(
  value: number,
  passMax: number,
  warnMax: number,
  format: (v: number) => string | number = (v): number => v,
  locations?: string[],
): SeoRuleOutcome {
  const threshold = `≤ ${passMax}`;
  if (value <= passMax) return pass(format(value), threshold, locations);
  if (value <= warnMax) return warn(format(value), threshold, locations);
  return fail(format(value), threshold, locations);
}

/** Statut « plus grand = mieux » : PASS ≥ a, WARN ≥ b, FAIL en dessous. */
export function minStatus(
  value: number,
  passMin: number,
  warnMin: number,
  format: (v: number) => string | number = (v): number => v,
  locations?: string[],
): SeoRuleOutcome {
  const threshold = `≥ ${passMin}`;
  if (value >= passMin) return pass(format(value), threshold, locations);
  if (value >= warnMin) return warn(format(value), threshold, locations);
  return fail(format(value), threshold, locations);
}

export const pct = (ratio: number): string => `${Math.round(ratio * 1000) / 10} %`;
export const clip = (text: string, max = 80): string => (text.length > max ? `${text.slice(0, max - 1)}…` : text);
